// UnsavedChangesDialog — RC-1 Commercial QA · Unsaved Changes Protection.
// Presentation-only. Renders a modal confirmation with three actions:
//   • Save and Leave (primary)
//   • Discard Changes
//   • Cancel
// The caller wires the three handlers from `useUnsavedChanges`.
import React, { useEffect } from "react";
import { AlertTriangle, Save, X, Trash } from "lucide-react";

export default function UnsavedChangesDialog({
  open,
  onSaveAndLeave,
  onDiscard,
  onCancel,
}) {
  // Close-on-escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onCancel?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  // z-[10050] must clear the bottom navigation (zIndex 10000, Layout.jsx)
  // and the FloatingActionToolbar (zIndex 10001), which otherwise paint over
  // this dialog and swallow clicks on its buttons at mobile width. Same
  // value and rationale as CreateKillSheetFromOpStateModal and
  // PullFromProfilesDrawer.
  return (
    <div data-testid="unsaved-changes-dialog"
         className="fixed inset-0 z-[10050] flex items-center justify-center p-4"
         style={{ background: "rgba(0,0,0,0.66)", backdropFilter: "blur(4px)" }}
         role="dialog"
         aria-modal="true"
         aria-labelledby="unsaved-changes-title">
      <div className="card-rs w-full max-w-md p-5"
           style={{ background: "var(--rs-surface-1,#0d1117)", borderColor: "rgba(245,180,0,0.5)" }}>
        <div className="flex items-start gap-3">
          <div className="shrink-0 mt-0.5">
            <AlertTriangle className="w-5 h-5" style={{ color: "var(--rs-amber, #f5b400)" }} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="unsaved-changes-title" className="text-[15px] font-semibold text-[var(--rs-text-1)]">
              Unsaved Changes
            </h2>
            <p className="mt-1 text-[13px] text-[var(--rs-text-2)] leading-snug">
              You have unsaved changes.<br />
              Would you like to save your work before leaving?
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-2">
          <button type="button"
                  onClick={onCancel}
                  data-testid="unsaved-cancel"
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[12.5px] font-medium text-[var(--rs-text-2)] border border-[var(--rs-border,#1f2630)] hover:bg-[var(--rs-surface-2,#12161d)] order-3 sm:order-1">
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          <button type="button"
                  onClick={onDiscard}
                  data-testid="unsaved-discard"
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[12.5px] font-medium text-red-300 border border-red-500/40 hover:bg-red-500/10 order-2">
            <Trash className="w-3.5 h-3.5" /> Discard Changes
          </button>
          <button type="button"
                  onClick={onSaveAndLeave}
                  data-testid="unsaved-save-leave"
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-semibold order-1 sm:order-3"
                  style={{ background: "var(--rs-amber, #f5b400)", color: "#0A0B0E" }}>
            <Save className="w-4 h-4" /> Save and Leave
          </button>
        </div>
      </div>
    </div>
  );
}
